import { useQuery } from '@tanstack/react-query';
import { UserIdentitiesQueryKeys } from '../../queryKeys';
import {
  UseUserIdentitiesLookUpProps,
  UseUserIdentitiesLookUpQueryKeyProps,
} from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { Nullable } from '@starsoft/common/interfaces';
import { fetchUserIdentitiesLookup } from './service';
import { useMemo } from 'react';
import { FetchUserIdentitiesLookUpInputDto } from '@starsoft/common/dto';
import { GenericError, UserIdentity } from '@starsoft/common/models';
import { UserIdentityStatus } from '@starsoft/common/enums';
import { UserIdentityFormFields } from '../../../../enums/user-identity/form-fields';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to lookup and validate user identity information
 *
 * This hook handles fetching and validating user identity data based on an identifier and origin.
 * It uses React Query for data fetching, caching, and state management.
 * The hook also manages error states and provides a map of validation errors.
 *
 * @param {UseUserIdentitiesLookUpProps} options - Optional configuration object
 * @param {string} options.identifier - The identity identifier to lookup (e.g. tax ID, document number)
 * @param {string} options.originId - The origin/source of the identity
 * @param {boolean} options.enabled - Optional flag to enable/disable the query (defaults to true)
 *
 * @returns {Object} An object containing:
 *   - data: The fetched user identity data or null
 *   - isLoading: Boolean indicating if fetch is in progress (excluding idle state)
 *   - isFetching: Boolean indicating if a fetch is in progress
 *   - isError: Boolean indicating if an error occurred
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually trigger a refetch
 *   - errorFieldsMap: Map containing validation errors for specific form fields
 */
export function useUserIdentityLookUp(options?: UseUserIdentitiesLookUpProps) {
  const { identifier, originId, enabled = true } = options ?? {};

  const filter: FetchUserIdentitiesLookUpInputDto = {
    //@ts-ignore
    identifier: identifier?.replaceAll?.('-', '')?.replaceAll?.('.', ''),
    originId,
  };

  const queryKey: UseUserIdentitiesLookUpQueryKeyProps = [
    UserIdentitiesQueryKeys.Lookup,
    filter,
  ];
  const axiosInstance: AxiosInstance = useAxiosInstance();

  const { data, isPending, isError, error, refetch, isFetching, fetchStatus } =
    useQuery<
      Nullable<UserIdentity>,
      AxiosError<GenericError>,
      Nullable<UserIdentity>,
      UseUserIdentitiesLookUpQueryKeyProps
    >({
      queryKey,
      queryFn: queryContext =>
        fetchUserIdentitiesLookup(queryContext, axiosInstance),
      enabled: !!identifier && !!originId && enabled,
      staleTime: Infinity,
      retry: 0,
    });

  // Create and memoize a map of validation errors for form fields
  const errorFieldsMap: Map<UserIdentityFormFields, string[]> = useMemo(() => {
    const map: Map<UserIdentityFormFields, string[]> = new Map();

    if (!!data && data?.status === UserIdentityStatus.Invalid) {
      map.set(UserIdentityFormFields.TaxId, []);
    }

    return map;
  }, [data]);

  return {
    data,
    isLoading: isPending && fetchStatus !== 'idle',
    isFetching,
    isError,
    error,
    refetch,
    errorFieldsMap,
  };
}
